import { useBoolean } from '@blockworks/platform/hooks';
import { FlexBox } from '@blockworks/ui/flex-box';
import { InfoCircleIcon } from '@blockworks/ui/icon';
import { IconButton } from '@blockworks/ui/icon-button';
import { Modal } from '@blockworks/ui/modal';
import { Text } from '@blockworks/ui/text';

import { MarkdownRenderer } from '../../../markdown';
import type { VisualizationRendererProps } from '../visualization-renderer/visualization-renderer.model';

import { VISUALIZATION_HEADER_HTML_ID } from './visualization-header.model';

type VisualizationHeaderProps = {
    title: {
        label: string;
        Component?: NonNullable<NonNullable<VisualizationRendererProps['componentSlots']>['HeaderTitle']>;
    };
    slug?: string;
    description?: string;
    subTitle?: string;
    queryId: string;
    visualizationId: string;
};

const VisualizationHeader = ({
    title,
    description,
    subTitle,
    queryId,
    visualizationId,
    slug,
}: VisualizationHeaderProps) => {
    const modal = useBoolean(false);
    const {
        Component = props => (
            <Text as="span" size="xs" weight="semibold" lines={1} truncate>
                {props.title}
            </Text>
        ),
        label,
    } = title;

    return (
        <FlexBox alignItems="center" gap={1} maxW="full" id={VISUALIZATION_HEADER_HTML_ID}>
            <FlexBox col maxW="full">
                <FlexBox alignItems="center" gap={0.5} maxW="full">
                    <Component queryId={queryId} title={label} visualizationId={visualizationId} slug={slug} />
                    {description && (
                        <IconButton
                            onClick={modal.setTrue}
                            aria-label="View Description"
                            flexGrow={0}
                            flexShrink={0}
                            variant="ghost"
                            size="2xs"
                            classNames={{
                                base: 'inline-flex h-4 w-4 hover:bg-transparent data-selected:bg-transparent active:bg-transparent',
                                icon: 'h-[0.7rem] w-[0.7rem]',
                            }}
                            icon={InfoCircleIcon}
                        />
                    )}
                </FlexBox>
                {subTitle?.trim?.() && (
                    <Text
                        as="p"
                        size="xxs"
                        color="muted"
                        className="-mt-1 relative top-[1px] inline-block"
                        lines={1}
                        truncate
                    >
                        {subTitle}
                    </Text>
                )}
            </FlexBox>
            <Modal
                classNames={{ base: 'w-[600px] max-w-[90vw]' }}
                shadow="md"
                onOpenChange={modal.setValue}
                open={modal.value}
            >
                <Modal.Header closeButtonSize="xs">
                    <Modal.Title size="sm">
                        <Text>{label}</Text>
                        {subTitle && (
                            <Text size="xs" weight="regular" color="muted" className="-mt-0.5">
                                {subTitle}
                            </Text>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="gap-4 max-h-[75vh]">
                    {description && <MarkdownRenderer.Memoized markdown={description} />}
                </Modal.Body>
            </Modal>
        </FlexBox>
    );
};
export { VisualizationHeader };
